import type { IDistributionBucket, QuestionAnswerSummary } from '@brushfire/types'

/** Figma 5897:13785 — section layout spacing (px). */
export const QUESTION_SUMMARY_LAYOUT = {
  sectionPadding: '32px',
  sectionGap: '24px',
  headerGap: '16px',
  headerMetaGap: '4px',
  metricsTopPadding: '16px',
  metricsGridGap: '24px',
  metricsGridRowHeight: '118.5px',
  metricTilePadding: '21px',
  metricTileInnerGap: '4px',
  metricTileRadius: '12px',
} as const

/** Figma 5897:13785 — question summary card typography and metric tile palette. */
export const QUESTION_SUMMARY_SECTION_SX = {
  bgcolor: '#FFFFFF',
  borderRadius: '16px',
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
  p: QUESTION_SUMMARY_LAYOUT.sectionPadding,
  boxSizing: 'border-box',
} as const

export const QUESTION_SUMMARY_Q_BADGE_SX = {
  width: 40,
  height: 40,
  borderRadius: '8px',
  bgcolor: '#006192',
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '21px',
  flexShrink: 0,
} as const

export const QUESTION_SUMMARY_TEXT_SX = {
  fontWeight: 700,
  fontSize: '32px',
  lineHeight: '40px',
  letterSpacing: '-0.64px',
  color: '#313032',
  m: 0,
} as const

export const QUESTION_SUMMARY_METRIC_LABEL_SX = {
  fontWeight: 600,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: '#000000',
  fontFamily: '"Inter", sans-serif',
  m: 0,
} as const

export const QUESTION_SUMMARY_METRIC_VALUE_SX = {
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '28px',
  letterSpacing: '-0.2px',
  fontFamily: '"Inter", sans-serif',
} as const

export const QUESTION_SUMMARY_METRIC_SUB_SX = {
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  fontFamily: '"Inter", sans-serif',
} as const

export const QUESTION_SUMMARY_METRIC_TILES = {
  avgTime: {
    bgcolor: '#F3E8FF',
    border: '1px solid rgba(197, 195, 229, 0.2)',
    valueColor: '#7E22CE',
    subColor: '#583E8B',
  },
  majorityPreference: {
    bgcolor: '#DCFCE7',
    border: '1px solid rgba(173, 198, 255, 0.2)',
    valueColor: '#0D9467',
    subColor: '#0D9467',
  },
  engagement: {
    bgcolor: '#DCFCE7',
    border: '1px solid rgba(173, 198, 255, 0.2)',
    valueColor: '#0D9467',
    subColor: '#0D9467',
  },
  engagementAverage: {
    bgcolor: '#FEF3C7',
    border: '1px solid rgba(251, 191, 36, 0.2)',
    valueColor: '#B45309',
    subColor: '#B45309',
  },
  logicFlow: {
    bgcolor: '#DBEAFE',
    border: '1px solid rgba(227, 192, 164, 0.2)',
    valueColor: '#2563EB',
    subColor: '#2563EB',
  },
  totalFeedback: {
    bgcolor: '#FFEDD5',
    border: '1px solid rgba(255, 180, 171, 0.2)',
    valueColor: '#9A3412',
    subColor: '#9A3412',
  },
} as const

/** Figma 5897:14481 — `{instrumentName} - Q{order}: {questionText}` under the page title. */
export function formatQuestionDrillDownSubtitle(
  instrumentName: string,
  order: number,
  questionText: string,
): string {
  return `${instrumentName} — Q${order}: ${questionText}`
}

export const QUESTION_DRILLDOWN_TITLE_SX = {
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '32px',
  letterSpacing: '0.0703px',
  color: '#0F172A',
} as const

/** Figma 5897:14481 — instrument name + question context under the page title. */
export const QUESTION_DRILLDOWN_SUBTITLE_SX = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#008CD0',
} as const

export function formatQuestionAvgCompletionTime(
  totalSeconds: number | null | undefined,
): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return 'N/A'
  }
  const secs = Math.max(0, Math.round(totalSeconds))
  const m = Math.floor(secs / 60)
  const s = secs % 60
  if (m > 0) return `${m}m ${s}s`
  return `${secs} sec${secs === 1 ? '' : 's'}`
}

const QUANTITATIVE_SUMMARY_TYPES = new Set([
  'multiple_choice',
  'yes_no',
  'dropdown',
  'picture_choice',
  'likert',
  'rating',
])

/** Derives Majority / Leading summary from drill-down distribution buckets. */
export function answerSummaryFromDistribution(
  questionType: string,
  global: IDistributionBucket[],
): QuestionAnswerSummary {
  if (!QUANTITATIVE_SUMMARY_TYPES.has(questionType.trim().toLowerCase()) || global.length === 0) {
    return { kind: 'na' }
  }

  const total = global.reduce((sum, bucket) => sum + bucket.count, 0)
  if (total <= 0) return { kind: 'na' }

  const top = [...global].sort((a, b) => b.count - a.count)[0]
  const percent = Math.round((top.count / total) * 100)
  const answerLabel = top.label?.trim() || top.key

  if (percent > 50) {
    return { kind: 'majority', answerLabel, percent }
  }
  return { kind: 'leading', answerLabel, percent }
}

export function formatMajorityPreferenceValue(summary: QuestionAnswerSummary): {
  value: string
  suffix?: string
} {
  if (summary.kind === 'na') return { value: 'N/A' }
  return { value: summary.answerLabel, suffix: `— ${summary.percent}%` }
}
